import { initializeApp } from "firebase/app";
// As duas ligacoes com o emulador. Elas sao importadas sempre e CHAMADAS so atras da tranca de
// desenvolvimento abaixo — o eliminador de codigo morto tira a chamada do build de producao, e o
// import fica sem custo porque o SDK ja esta no pacote.
import { connectAuthEmulator } from "firebase/auth";
import { connectFirestoreEmulator } from "firebase/firestore";
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  updateProfile,
  User,
  verifyBeforeUpdateEmail,
  verifyPasswordResetCode,
} from "firebase/auth";
import {
  addDoc,
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentData,
  DocumentSnapshot,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  query,
  Query,
  QueryCompositeFilterConstraint,
  QueryFieldFilterConstraint,
  QuerySnapshot,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { CreateAppUserObj } from "./app-user.class";
import { enviarVerificacao } from "./verificacao";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Guard: nao inicializa Firebase se env vars vazias — previne crash em runtime.
export const firebaseOn = !!firebaseConfig.apiKey;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let app: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let db: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _auth: any = null;

if (firebaseOn) {
  app = initializeApp(firebaseConfig);
  _auth = getAuth(app);
  db = getFirestore(app);

  // ─── DEV-ONLY BRIDGE (spec 055 · padrao Dora 2026-08-03) ─────────────────
  // Expoe { app, auth, db } em window.__mvp_bridge PARA Playwright rodando
  // contra dev server local (npm start @ localhost:3000). react-scripts
  // remove branch em production build via dead-code elimination.
  if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__mvp_bridge = { app, auth: _auth, db };
  }

  // ─── O EMULADOR, E POR QUE ELE NAO E A VITRINE ────────────────────────────────────────
  //
  // A vitrine SUBSTITUI o Firebase: ela fabrica um usuario e uma lista de quadrinhos, e por isso
  // mede tela e nao mede persistencia. O cabecalho dela diz isso por escrito.
  //
  // O emulador nao substitui nada: ele E o Firestore e o Auth, rodando local, com o MESMO motor de
  // regras que producao usa e carregando o `firestore.rules` deste repositorio. Salvar nele salva de
  // verdade; recarregar a pagina le de verdade; uma segunda conta escrevendo no quadrinho da primeira
  // e recusada pelo motor, e nao por um `if` que alguem escreveu no teste.
  //
  // O QUE ELE CONTINUA NAO PROVANDO, e isso vai escrito em toda medicao feita aqui: que as regras
  // DEPLOYADAS sao as deste arquivo, e que a rede de producao se comporta assim. As duas coisas
  // exigem producao, e nenhuma delas e sobre o codigo do produto.
  //
  // AS DUAS TRANCAS, na mesma ordem da vitrine e pelo mesmo motivo: a checagem de ambiente vem
  // primeiro e como literal, para o eliminador de codigo morto provar que o branch e inalcancavel
  // num build de producao. Inverter isto poria a ligacao com um servidor local dentro do pacote que
  // a crianca baixa.
  if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_EMULADOR === "sim") {
    // `disableWarnings` porque o aviso do SDK e um banner amarelo sobre a tela, e ele atrapalha
    // exatamente as medicoes de layout que esta bancada faz. A tranca de fora e a garantia real.
    connectAuthEmulator(_auth, "http://127.0.0.1:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, "127.0.0.1", 8085);
    // eslint-disable-next-line no-console
    console.warn("[HQ-Lab] EMULADOR LIGADO — auth em 9099, firestore em 8085. Nada daqui vai para producao.");
  }
} else {
  // eslint-disable-next-line no-console
  console.warn("[HQ-Lab] Firebase NAO configurado (REACT_APP_FIREBASE_API_KEY vazia). Auth desativada.");
}

export const auth = _auth;

export default class Firebase {
  /**
   * O AVISO DIZIA "AUTH DESATIVADA" E O CÓDIGO CHAMAVA A AUTH ASSIM MESMO.
   *
   * Sem `REACT_APP_FIREBASE_API_KEY`, o bloco acima deixa `_auth` em `null` e imprime
   * "Firebase NAO configurado. Auth desativada." — mas este método chamava
   * `onAuthStateChanged(null, cb)` e o SDK lançava `Cannot read properties of null`. O
   * `useEffect` do `global-state-context` é o primeiro a rodar, então o erro subia antes de
   * qualquer tela montar e o `LimiteDeErro` engolia o app inteiro: "Alguma coisa deu errado."
   *
   * Achado em 2026-09-12 tentando MEDIR o estado deslogado — a única forma de rodar o produto sem
   * sessão é rodar sem Firebase, e era justamente o caminho que não funcionava. Ou seja: o defeito
   * escondia a si mesmo, porque escondia o único cenário em que ele aparece.
   *
   * Em produção a chave existe e isto nunca acontece. Quem sofria era quem clona o repositório e
   * roda `npm start` sem configurar nada — e recebia uma tela de erro sem relação com a causa.
   *
   * O conserto respeita o aviso: sem auth, não há estado de autenticação para observar. Avisa a
   * quem chama que o usuário é `null` e devolve um cancelamento que não faz nada, que é o que a
   * assinatura promete.
   */
  static onAuthStateChanged(callback: (user: User | null) => any) {
    if (!auth) {
      callback(null);
      return () => {};
    }
    return onAuthStateChanged(auth, callback);
  }

  static async login(email: string, password: string) {
    /*await auth.setPersistence(
    rememberMe ? browserLocalPersistence : browserSessionPersistence
    );*/
    const user = (await signInWithEmailAndPassword(auth, email, password)).user;
    if (!user.emailVerified) {
      await signOut(auth);
      // eslint-disable-next-line
      throw { code: "auth/email-not-verified" };
    }

    // return user;
  }

  /**
   * T26.R2 · REENVIAR A CONFIRMAÇÃO. O relatório de 09/10: «sem confirmar o e-mail não entro (...)
   * Não existe botão para reenviar a confirmação». O Firebase só reenvia para uma sessão aberta,
   * então entra com o e-mail e a senha que a pessoa acabou de digitar, manda o e-mail e sai — a
   * conta continua sem entrar até o link ser clicado.
   */
  /**
   * Ticket 0055 · O E-MAIL COM A CARA DO HQ-LAB. Pede ao serviço de e-mail da casa o e-mail de
   * confirmação com o visual do tema quadrinho (mvp-email-api /api/verificacao/enviar); se ele não
   * responder 200, manda o padrão do Firebase — com volta para a tela de entrar. Tem que rodar ANTES
   * do signOut: o serviço confere quem pede pelo token da sessão aberta.
   */
  static async enviarConfirmacao(user: User) {
    await enviarVerificacao(user, (u) =>
      sendEmailVerification(u, { url: `${window.location.origin}/login` })
    );
  }

  static async reenviarConfirmacao(email: string, password: string) {
    const user = (await signInWithEmailAndPassword(auth, email, password)).user;
    try {
      if (!user.emailVerified) await Firebase.enviarConfirmacao(user);
    } finally {
      await signOut(auth);
    }
  }

  /**
   * T29.5 · TROCAR O E-MAIL DE UMA CONTA QUE NUNCA FOI CONFIRMADA. O relatório de 14/10: «E-mail
   * não confirmado vira beco sem saída: permitir corrigir o e-mail ou dizer para criar outra
   * conta». Quem errou o endereço no cadastro reenviava a confirmação para o endereço errado, para
   * sempre.
   *
   * Entra com o e-mail e a senha que a pessoa acabou de digitar (como o reenviar), e pede ao
   * Firebase para mandar o link ao endereço NOVO: clicar nele troca o e-mail da conta e já a deixa
   * confirmada. Até lá a conta continua com o e-mail antigo, e sem entrar.
   */
  static async trocarOEmailNaoConfirmado(email: string, password: string, novoEmail: string) {
    const user = (await signInWithEmailAndPassword(auth, email, password)).user;
    try {
      // eslint-disable-next-line
      if (user.emailVerified) throw { code: "auth/email-ja-confirmado" };
      await verifyBeforeUpdateEmail(user, novoEmail.trim(), { url: `${window.location.origin}/login` });
    } finally {
      await signOut(auth);
    }
  }

  static async loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Check if user doc exists in Firestore, create if not
    const userDocRef = doc(db, "users", user.uid);
    const { getDoc } = await import("firebase/firestore");
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      const userData: CreateAppUserObj & { _U: string } = {
        name: user.displayName || "",
        email: user.email || "",
        city: "",
        educationalInstitution: "",
        birthDate: "",
        _U: "",
      };
      await setDoc(userDocRef, userData);
    }
  }

  static logOut() {
    return signOut(auth);
  }

  static async sendRecoverPasswordEmail(email: string) {
    return await sendPasswordResetEmail(auth, email);
  }
  static async updatePassword(
    firestoreUser: User,
    password: string,
    newPassword: string
  ) {
    const credential = EmailAuthProvider.credential(
      firestoreUser.email!,
      password
    );
    await reauthenticateWithCredential(firestoreUser, credential);
    await updatePassword(firestoreUser, newPassword);
  }
  static async resetPassword(oobCode: string, newPassword: string) {
    await verifyPasswordResetCode(auth, oobCode);
    await confirmPasswordReset(auth, oobCode, newPassword);
  }

  // ── ESCUTAS ────────────────────────────────────────────────────────────────────────────────
  //
  // Duas coisas que estes dois metodos passaram a fazer, e o motivo de cada uma:
  //
  // 1. NAO ESTOURAR COM O FIREBASE DESLIGADO. `firebaseOn` ja existia e ja avisava no console,
  //    mas toda chamada abaixo dele seguia direto para `collection(db, ...)` com `db` indefinido,
  //    e o SDK lanca. Lancar durante o render de um componente derruba a arvore inteira: medido
  //    em `/biblioteca`, com o Firebase sem chave, a plataforma ficava em branco. Agora a escuta
  //    devolve uma inscricao vazia e a tela cai sozinha no caminho de "nao consegui carregar",
  //    que ela ja sabia mostrar.
  //
  // 2. TER CAMINHO DE ERRO. `onSnapshot` sem terceiro argumento engole falha de permissao: a
  //    tela fica girando para sempre e o unico sinal e uma linha vermelha no console, que nenhum
  //    aluno vai ler. Regra do Firestore negando leitura e o caso comum, nao o raro.
  static onDocSnapshot(
    path: string,
    id: string,
    onValue: (doc: DocumentSnapshot<DocumentData>) => any,
    onError?: (erro: Error) => void
  ) {
    if (!firebaseOn) return Firebase.escutaVazia(`${path}/${id}`, onError);
    const dbRef = doc(db, path, id);
    return onSnapshot(dbRef, onValue, (e) => Firebase.relatarErro(path, e, onError));
  }

  /**
   * `where` aceita filtro SIMPLES alem do composto, e essa mudanca tem historia.
   *
   * A `/biblioteca` listava a colecao inteira e filtrava `estado === "publicado"` em JavaScript.
   * Funcionava contra um Firestore sem regra — porque sem regra nada e negado nem permitido, a
   * colecao simplesmente nao existia. Com a regra publicada, a consulta passa a ser NEGADA:
   * o Firestore avalia a regra contra a CONSULTA, nao contra os documentos que voltariam, e uma
   * listagem sem `where` nao consegue provar que so pediu o que e publico.
   *
   * O erro do emulador diz isso na cara, e e facil de ler errado:
   *   "Property publicacao is undefined on object. for 'list'"
   * Ele nao esta reclamando de um documento sem `publicacao` — esta dizendo que, para uma
   * LISTAGEM, ele nao tem documento nenhum contra o qual avaliar `resource.data.publicacao`.
   *
   * Achado por validacao independente medindo a consulta EXATA da tela contra o emulador. Sem
   * isso, o deploy das regras teria "consertado" a biblioteca deixando-a exatamente tao vazia
   * quanto antes — e com uma explicacao pior, porque agora pareceria configurada.
   */
  static onCollectionSnapshot(
    path: string,
    onValue: (result: QuerySnapshot<DocumentData>) => any,
    where?: QueryCompositeFilterConstraint | QueryFieldFilterConstraint,
    limitValue?: number,
    onError?: (erro: Error) => void
  ) {
    if (!firebaseOn) return Firebase.escutaVazia(path, onError);
    let dbRef: Query<DocumentData> | CollectionReference<DocumentData> =
      collection(db, path);
    if (!!where) {
      // O `query` do SDK tem sobrecargas separadas para filtro composto e simples, e o tipo uniao
      // nao casa com nenhuma delas sozinho. O `as any` estreita para a chamada que o runtime
      // aceita nos dois casos — e esta isolado nesta linha, com o tipo do parametro preservado.
      dbRef = query(dbRef, where as any);
    }
    if (!!limitValue) {
      dbRef = query(dbRef, limit(limitValue));
    }
    return onSnapshot(dbRef, onValue, (e) => Firebase.relatarErro(path, e, onError));
  }

  /** Inscricao que nao escuta nada, para quando nao ha base com que falar. */
  private static escutaVazia(path: string, onError?: (erro: Error) => void) {
    const erro = new Error(
      `Firebase não configurado: a escuta de "${path}" não foi aberta.`
    );
    console.warn("[HQ-Lab] " + erro.message);
    // Assincrono de proposito: chamar de volta durante o proprio `useEffect` que abriu a escuta
    // faria o componente mudar de estado no meio da montagem.
    if (onError) setTimeout(() => onError(erro), 0);
    return () => {};
  }

  private static relatarErro(
    path: string,
    e: Error,
    onError?: (erro: Error) => void
  ) {
    console.error(`[HQ-Lab] escuta de "${path}" falhou:`, e);
    onError?.(e);
  }

  /**
   * LEITURA DE UMA VEZ, e não escuta.
   *
   * A classe só tinha `onDocSnapshot`/`onCollectionSnapshot` — escutas. Escuta serve à tela que
   * precisa reagir a mudança de outra pessoa (a biblioteca, o acervo). Ela é o instrumento errado
   * para "quais instituições existem?": a lista muda quando a equipe provisiona uma escola nova,
   * o que acontece uma vez por semana, e manter uma escuta aberta para isso paga conexão o tempo
   * inteiro por uma resposta que não muda.
   *
   * Devolve `null` (ou lista vazia) quando o Firebase não está configurado, em vez de lançar — a
   * mesma decisão do `escutaVazia`, e pela mesma razão: o produto tem que abrir sem base.
   */
  static async getDocument(path: string, id: string): Promise<DocumentData | null> {
    if (!firebaseOn) {
      console.warn(`[HQ-Lab] Firebase nao configurado: leitura de "${path}/${id}" devolveu null.`);
      return null;
    }
    const s = await getDoc(doc(db, path, id));
    return s.exists() ? { id: s.id, ...s.data() } : null;
  }

  /**
   * Uma página de uma coleção, com filtro OPCIONAL.
   *
   * O filtro não é conveniência: contra regra publicada, uma listagem SEM `where` costuma ser
   * NEGADA, porque o Firestore avalia a regra contra a CONSULTA e uma consulta sem filtro não
   * consegue provar que só pediu o que é permitido. A lição inteira está no comentário do
   * `onCollectionSnapshot`, e ela custou uma biblioteca vazia que parecia configurada.
   */
  static async getCollection(
    path: string,
    filtros: QueryFieldFilterConstraint[] = [],
    limitValue?: number
  ): Promise<DocumentData[]> {
    if (!firebaseOn) {
      console.warn(`[HQ-Lab] Firebase nao configurado: leitura de "${path}" devolveu lista vazia.`);
      return [];
    }
    let dbRef: Query<DocumentData> | CollectionReference<DocumentData> = collection(db, path);
    if (filtros.length) dbRef = query(dbRef, ...filtros);
    if (limitValue) dbRef = query(dbRef, limit(limitValue));
    const s = await getDocs(dbRef);
    return s.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  static async setDocument(path: string, id: string, data: any) {
    const dbRef = doc(db, path, id);
    return await setDoc(dbRef, data);
  }
  static async pushDocument(path: string, data: any) {
    const dbRef = collection(db, path);
    return await addDoc(dbRef, data);
  }
  static async updateDocument(path: string, id: string, data: any) {
    const dbRef = doc(db, path, id);
    return await updateDoc(dbRef, data);
  }
  static async deleteDocument(path: string, id: string) {
    const dbRef = doc(db, path, id);
    return await deleteDoc(dbRef);
  }

  static async createUser(
    data: CreateAppUserObj,
    password: string,
    /** T18.9 · roda com a conta recém-criada, antes de sair — é onde nasce o pedido de vínculo. */
    depoisDeCriar?: (uid: string) => Promise<void>
  ) {
    const user = (
      await createUserWithEmailAndPassword(auth, data.email, password)
    ).user;
    await updateProfile(user, { displayName: data.name });
    await Firebase.enviarConfirmacao(user);

    const _data = { ...data, _U: "" };
    await Firebase.setDocument("users", user.uid, _data);

    if (depoisDeCriar) {
      // O vínculo é opcional (regras de 06/10): se o pedido falhar, a conta continua criada e a
      // pessoa pede depois, em Minha conta.
      try {
        await depoisDeCriar(user.uid);
      } catch (e) {
        console.warn("[cadastro] o pedido de vínculo não saiu; dá para pedir em Minha conta", e);
      }
    }

    await signOut(auth);
  }
  static async deleteUser(firestoreUser: User, password: string) {
    const { uid } = firestoreUser;

    const credential = EmailAuthProvider.credential(
      firestoreUser.email!,
      password
    );
    await reauthenticateWithCredential(firestoreUser, credential);

    const collectionDeletePromises: Promise<void>[] = [];
    for (const collectionName of ["comics", "imgs", "generatedImages"]) {
      const collectionQuery = query(
        collection(db, `users/${uid}/${collectionName}`)
      );
      const querySnapshot = await getDocs(collectionQuery);
      querySnapshot.forEach((doc) =>
        collectionDeletePromises.push(deleteDoc(doc.ref))
      );
    }
    await Promise.all(collectionDeletePromises);

    const dbRef = doc(db, "users", uid);
    await deleteDoc(dbRef);

    await deleteUser(firestoreUser);
  }
}
